import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, CheckCircle, Edit3, ChevronDown, ChevronRight, Trash2, HelpCircle } from "lucide-react";
import { Button, Card, Badge, Spinner, Modal, Input, Textarea, Select } from "../shared/ui";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Colour dot config: green paid, orange unpaid <14d, red unpaid >14d, blue no final sent, grey nothing
const COLOUR_MAP = {
  green:  { bg: 'bg-green-500',  label: 'Paid in full & final invoice sent' },
  orange: { bg: 'bg-orange-500', label: 'Invoiced, awaiting payment (< 14 days)' },
  red:    { bg: 'bg-red-500',    label: 'Invoice unpaid > 14 days — OVERDUE' },
  blue:   { bg: 'bg-blue-500',   label: 'Job finished, final invoice not sent' },
  grey:   { bg: 'bg-slate-400',  label: 'No invoicing activity yet' },
};

const StatusDot = ({ colour, size = 'default' }) => {
  const cfg = COLOUR_MAP[colour] || COLOUR_MAP.grey;
  const cls = size === 'lg' ? 'w-4 h-4' : 'w-3 h-3';
  return (
    <span
      className={`inline-block ${cls} rounded-full ${cfg.bg} shadow-sm ring-2 ring-white`}
      title={cfg.label}
      data-testid={`status-dot-${colour}`}
    />
  );
};

const ColourLegend = () => (
  <div className="rounded-lg border-2 border-slate-200 bg-slate-50 p-3" data-testid="invoicing-legend">
    <div className="flex items-center gap-2 mb-2">
      <HelpCircle className="w-4 h-4 text-slate-500" />
      <span className="text-xs font-bold uppercase text-slate-600">Status Key</span>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs">
      {Object.entries(COLOUR_MAP).filter(([k]) => k !== 'grey').map(([k, v]) => (
        <div key={k} className="flex items-center gap-2">
          <span className={`inline-block w-3 h-3 rounded-full ${v.bg}`} />
          <span className="text-slate-700">{v.label}</span>
        </div>
      ))}
    </div>
  </div>
);

const InvoiceTypeBadge = ({ type }) => {
  const map = {
    progress:  { bg: 'bg-blue-100 text-blue-700 border-blue-300',       label: 'PROGRESS' },
    variation: { bg: 'bg-purple-100 text-purple-700 border-purple-300', label: 'VARIATION' },
    final:     { bg: 'bg-emerald-100 text-emerald-700 border-emerald-300', label: 'FINAL' },
  };
  const c = map[type] || map.progress;
  return <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${c.bg}`}>{c.label}</span>;
};

const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const InvoicingPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [filter, setFilter] = useState('all');

  // Create invoice modal
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceTarget, setInvoiceTarget] = useState(null); // {entity_type, entity_id, contract_amount, existing}
  const [invoiceForm, setInvoiceForm] = useState({
    invoice_type: 'progress', invoice_number: '', invoice_date: '',
    invoice_amount: '', due_date: '', description: '', notes: ''
  });
  const [editingInvoice, setEditingInvoice] = useState(null); // the invoice object being edited

  // Record payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentTargetInvoice, setPaymentTargetInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '', payment_date: '', payment_method: '', reference: '', notes: ''
  });

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/jobs/invoicing-summary`);
      setData(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreateInvoice = (entity, invoiceTypeDefault = 'progress') => {
    setEditingInvoice(null);
    setInvoiceTarget({ entity_type: entity.entity_type, entity_id: entity.entity_id });
    setInvoiceForm({
      invoice_type: invoiceTypeDefault, invoice_number: '',
      invoice_date: new Date().toISOString().split('T')[0],
      invoice_amount: '', due_date: '', description: '', notes: ''
    });
    setShowInvoiceModal(true);
  };

  const openEditInvoice = (entity, inv) => {
    setEditingInvoice(inv);
    setInvoiceTarget({ entity_type: entity.entity_type, entity_id: entity.entity_id });
    setInvoiceForm({
      invoice_type: inv.invoice_type || 'progress',
      invoice_number: inv.invoice_number || '',
      invoice_date: inv.invoice_date || '',
      invoice_amount: String(inv.invoice_amount || ''),
      due_date: inv.due_date || '',
      description: inv.description || '',
      notes: inv.notes || ''
    });
    setShowInvoiceModal(true);
  };

  const openRecordPayment = (inv) => {
    setPaymentTargetInvoice(inv);
    setPaymentForm({
      amount: '', payment_date: new Date().toISOString().split('T')[0],
      payment_method: '', reference: '', notes: ''
    });
    setShowPaymentModal(true);
  };

  const handleSaveInvoice = async () => {
    if (!invoiceTarget) return;
    const amt = parseFloat(invoiceForm.invoice_amount);
    if (!amt || amt <= 0) {
      toast.error("Invoice amount must be greater than zero");
      return;
    }
    try {
      if (editingInvoice) {
        await axios.put(`${API}/invoices/${editingInvoice.id}`, { ...invoiceForm, invoice_amount: amt });
        toast.success("Invoice updated");
      } else {
        await axios.post(
          `${API}/jobs/${invoiceTarget.entity_id}/invoices?entity_type=${invoiceTarget.entity_type}`,
          { ...invoiceForm, invoice_amount: amt }
        );
        toast.success(`${invoiceForm.invoice_type === 'variation' ? 'Variation' : 'Invoice'} created`);
      }
      setShowInvoiceModal(false);
      setEditingInvoice(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save invoice");
    }
  };

  const handleDeleteInvoice = async (inv) => {
    if (!window.confirm(`Delete invoice "${inv.invoice_number || inv.description || inv.id}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/invoices/${inv.id}`);
      toast.success("Invoice deleted");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete invoice");
    }
  };

  const handleSavePayment = async () => {
    if (!paymentTargetInvoice) return;
    const amt = parseFloat(paymentForm.amount);
    if (!amt || amt <= 0) {
      toast.error("Payment amount must be greater than zero");
      return;
    }
    try {
      const res = await axios.post(`${API}/invoices/${paymentTargetInvoice.id}/payment`, {
        ...paymentForm, amount: amt
      });
      toast.success(`Payment recorded! Invoice total paid: ${money(res.data.total_paid)}`);
      setShowPaymentModal(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record payment");
    }
  };

  const [bucketTab, setBucketTab] = useState('active'); // 'active' | 'paid'

  const filteredEntities = (data?.jobs || data?.entities || []).filter(e => {
    // Bucket split: active = not fully paid + final, paid = fully paid
    if (bucketTab === 'paid' && e.bucket !== 'paid') return false;
    if (bucketTab === 'active' && e.bucket !== 'active') return false;
    if (filter === 'all') return true;
    return e.status_colour === filter;
  });

  // Group by client_name for display
  const groupedByClient = filteredEntities.reduce((acc, e) => {
    const key = e.client_name || 'N/A';
    if (!acc[key]) acc[key] = [];
    acc[key].push(e);
    return acc;
  }, {});

  if (loading) return <div className="flex justify-center py-12"><Spinner /></div>;

  const summary = data?.summary || {};

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoicing-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold uppercase tracking-tight">Invoicing & Payments</h1>
          <p className="text-sm text-slate-500 mt-1">All jobs & hires with deposits, progress payments, variations and final invoicing — active until fully paid</p>
        </div>
      </div>

      {/* Summary Cards — entity counts by status (per-job amounts live on each card below) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="invoicing-summary-cards">
        <Card className="text-center p-3">
          <p className="text-2xl font-bold">{summary.total_entities || 0}</p>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Tracked</p>
          <p className="text-[9px] text-slate-400">{summary.total_active || 0} active · {summary.total_paid || 0} paid</p>
        </Card>
        <Card className="text-center p-3">
          <div className="flex items-center justify-center gap-2 mb-1">
            <StatusDot colour="green" size="lg" />
            <p className="text-2xl font-bold text-green-600">{summary.count_green || 0}</p>
          </div>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Paid in Full</p>
        </Card>
        <Card className="text-center p-3">
          <div className="flex items-center justify-center gap-2 mb-1">
            <StatusDot colour="orange" size="lg" />
            <p className="text-2xl font-bold text-orange-600">{summary.count_orange || 0}</p>
          </div>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Awaiting Payment</p>
        </Card>
        <Card className="text-center p-3">
          <div className="flex items-center justify-center gap-2 mb-1">
            <StatusDot colour="red" size="lg" />
            <p className="text-2xl font-bold text-red-600">{summary.count_red || 0}</p>
          </div>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Overdue &gt; 14 days</p>
        </Card>
        <Card className="text-center p-3">
          <div className="flex items-center justify-center gap-2 mb-1">
            <StatusDot colour="blue" size="lg" />
            <p className="text-2xl font-bold text-blue-600">{summary.count_blue || 0}</p>
          </div>
          <p className="text-[10px] text-slate-500 uppercase font-bold">Final Not Sent</p>
        </Card>
      </div>

      {/* Colour Legend */}
      <ColourLegend />

      {/* Active vs Paid tabs */}
      <div className="flex gap-2 border-b-2 border-slate-200" data-testid="invoicing-bucket-tabs">
        <button
          onClick={() => setBucketTab('active')}
          data-testid="bucket-tab-active"
          className={`px-4 py-2 font-bold text-sm uppercase -mb-[2px] border-b-2 ${bucketTab === 'active' ? 'border-orange-500 text-orange-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          Active ({summary.total_active || 0})
        </button>
        <button
          onClick={() => setBucketTab('paid')}
          data-testid="bucket-tab-paid"
          className={`px-4 py-2 font-bold text-sm uppercase -mb-[2px] border-b-2 ${bucketTab === 'paid' ? 'border-green-500 text-green-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          Paid ({summary.total_paid || 0})
        </button>
      </div>

      {/* Colour filter pills */}
      <div className="flex gap-2 flex-wrap items-center">
        <span className="text-xs font-bold uppercase text-slate-500 mr-1">Filter:</span>
        {[
          { v: 'all', l: 'All', n: summary.total_entities || 0 },
          { v: 'green', l: 'Paid', n: summary.count_green || 0 },
          { v: 'orange', l: 'Awaiting', n: summary.count_orange || 0 },
          { v: 'red', l: 'Overdue', n: summary.count_red || 0 },
          { v: 'blue', l: 'Final not sent', n: summary.count_blue || 0 },
        ].map(f => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            data-testid={`invoicing-filter-${f.v}`}
            className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase flex items-center gap-1.5 ${
              filter === f.v
                ? 'bg-slate-900 text-white border-2 border-slate-900'
                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
          >
            {f.v !== 'all' && <StatusDot colour={f.v} />}
            {f.l} <span className="opacity-70">({f.n})</span>
          </button>
        ))}
      </div>

      {/* Entity list grouped by client */}
      {filteredEntities.length === 0 ? (
        <Card className="text-center py-12">
          <p className="text-slate-500">
            {bucketTab === 'paid' ? 'No fully-paid jobs yet — once a job has a final invoice with all payments received, it\'ll land here.' : 'No jobs match this filter'}
          </p>
        </Card>
      ) : (
        <div className="space-y-5" data-testid="invoicing-entity-list">
          {Object.entries(groupedByClient).sort(([a],[b]) => a.localeCompare(b)).map(([clientName, items]) => {
            const clientOutstanding = items.reduce((sum, i) => sum + (i.outstanding || 0), 0);
            const clientPaid = items.reduce((sum, i) => sum + (i.total_paid || 0), 0);
            return (
              <div key={clientName} data-testid={`client-group-${clientName}`}>
                <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-300">
                  <h2 className="font-bold text-base uppercase text-slate-700">{clientName}</h2>
                  <div className="text-xs text-slate-500 flex items-center gap-3">
                    <span>{items.length} {items.length === 1 ? 'job' : 'jobs'}</span>
                    {clientOutstanding > 0 && <span className="text-orange-600 font-bold">Outstanding: {money(clientOutstanding)}</span>}
                    <span className="text-green-600 font-bold">Paid: {money(clientPaid)}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  {items.map(entity => {
            const isOpen = !!expanded[entity.entity_id];
            const cfg = COLOUR_MAP[entity.status_colour] || COLOUR_MAP.grey;
            const hasContract = (entity.contract_amount || 0) > 0;
            return (
              <Card
                key={`${entity.entity_type}-${entity.entity_id}`}
                className="border-2 border-slate-200"
                data-testid={`invoice-entity-${entity.entity_id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start gap-3">
                  {/* Colour dot + title block */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <StatusDot colour={entity.status_colour} size="lg" />
                      <h3 className="font-bold truncate">{entity.job_title}</h3>
                      {entity.entity_type === 'general_hire' && (
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-300">HIRE</span>
                      )}
                      <span className="text-xs text-slate-400" title={cfg.label}>{cfg.label}</span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      {entity.client_name}
                      {entity.completed_at ? ` — Completed ${new Date(entity.completed_at).toLocaleDateString('en-AU')}` : ''}
                      {entity.location ? ` · ${entity.location}` : ''}
                    </p>

                    {/* Financial summary row */}
                    <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm">
                      <span><span className="text-slate-500">Contract:</span> <strong>{money(entity.contract_amount)}</strong></span>
                      <span><span className="text-slate-500">Invoiced:</span> <strong className="text-blue-600">{money(entity.total_invoiced)}</strong></span>
                      {entity.total_variations > 0 && (
                        <span><span className="text-slate-500">Variations:</span> <strong className="text-purple-600">{money(entity.total_variations)}</strong></span>
                      )}
                      <span><span className="text-slate-500">Paid:</span> <strong className="text-green-600">{money(entity.total_paid)}</strong></span>
                      <span>
                        <span className="text-slate-500">Outstanding:</span>{' '}
                        <strong className={entity.outstanding > 0 ? 'text-orange-600' : 'text-slate-600'}>
                          {money(entity.outstanding)}
                        </strong>
                      </span>
                      {hasContract && entity.total_invoiced > 0 && (
                        <span className="text-slate-500">
                          ({Math.round((entity.total_invoiced / entity.contract_amount) * 100)}% of contract invoiced)
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      onClick={() => openCreateInvoice(entity, 'progress')}
                      data-testid={`add-progress-invoice-${entity.entity_id}`}
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Progress
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openCreateInvoice(entity, 'variation')}
                      data-testid={`add-variation-${entity.entity_id}`}
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Variation
                    </Button>
                    {!entity.has_final_invoice && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openCreateInvoice(entity, 'final')}
                        data-testid={`add-final-${entity.entity_id}`}
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" /> Final
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setExpanded(e => ({ ...e, [entity.entity_id]: !e[entity.entity_id] }))}
                      data-testid={`toggle-invoices-${entity.entity_id}`}
                    >
                      {isOpen ? <ChevronDown className="w-3.5 h-3.5 mr-1" /> : <ChevronRight className="w-3.5 h-3.5 mr-1" />}
                      {entity.invoice_count} invoice{entity.invoice_count === 1 ? '' : 's'}
                    </Button>
                  </div>
                </div>

                {/* Expanded invoice list */}
                {isOpen && (
                  <div className="mt-4 pt-4 border-t-2 border-dashed border-slate-200 space-y-2" data-testid={`invoices-list-${entity.entity_id}`}>
                    {entity.invoices.length === 0 ? (
                      <p className="text-sm text-slate-400 italic">No invoices yet — click "Progress" above to create the first one.</p>
                    ) : entity.invoices.map(inv => (
                      <div key={inv.id} className="rounded-lg border bg-slate-50 p-3" data-testid={`invoice-row-${inv.id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <InvoiceTypeBadge type={inv.invoice_type} />
                              <span className="font-bold text-sm">{inv.invoice_number || 'Draft'}</span>
                              <span className="text-xs text-slate-500">{inv.invoice_date}</span>
                              {inv.status === 'paid' && <Badge variant="success">Paid</Badge>}
                              {inv.status === 'partial' && <Badge variant="warning">Partial</Badge>}
                              {inv.status === 'sent' && (inv.amount_paid || 0) === 0 && <Badge variant="info">Sent</Badge>}
                            </div>
                            {inv.description && <p className="text-xs text-slate-600 mt-1">{inv.description}</p>}
                            <div className="flex gap-4 mt-1 text-xs">
                              <span>Amount: <strong>{money(inv.invoice_amount)}</strong></span>
                              <span>Paid: <strong className="text-green-600">{money(inv.amount_paid)}</strong></span>
                              <span>Outstanding: <strong className="text-orange-600">{money((inv.invoice_amount || 0) - (inv.amount_paid || 0))}</strong></span>
                              {inv.due_date && <span>Due: {inv.due_date}</span>}
                            </div>
                            {inv.payments?.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-slate-200 space-y-0.5">
                                {inv.payments.map((p, i) => (
                                  <div key={i} className="text-[11px] text-slate-500 flex gap-2 flex-wrap">
                                    <span>{p.payment_date}</span>
                                    <span className="text-green-600 font-semibold">{money(p.amount)}</span>
                                    {p.payment_method && <span>via {p.payment_method}</span>}
                                    {p.reference && <span>ref: {p.reference}</span>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col sm:flex-row gap-1.5">
                            {inv.status !== 'paid' && (
                              <Button size="sm" variant="outline" onClick={() => openRecordPayment(inv)} data-testid={`record-payment-${inv.id}`}>
                                <CheckCircle className="w-3.5 h-3.5 mr-1" /> Payment
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => openEditInvoice(entity, inv)} data-testid={`edit-invoice-${inv.id}`}>
                              <Edit3 className="w-3.5 h-3.5" />
                            </Button>
                            {(inv.amount_paid || 0) === 0 && (
                              <Button size="sm" variant="ghost" onClick={() => handleDeleteInvoice(inv)} data-testid={`delete-invoice-${inv.id}`}>
                                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                  </Card>
                );
              })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit invoice modal */}
      {showInvoiceModal && (
        <Modal
          isOpen={true}
          onClose={() => { setShowInvoiceModal(false); setEditingInvoice(null); }}
          title={editingInvoice
            ? 'Edit Invoice'
            : invoiceForm.invoice_type === 'variation' ? 'New Variation'
            : invoiceForm.invoice_type === 'final' ? 'Final Invoice'
            : 'Progress Payment Invoice'}
        >
          <div className="space-y-4" data-testid="invoice-modal">
            <Select
              label="Invoice Type"
              value={invoiceForm.invoice_type}
              onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_type: e.target.value })}
              options={[
                { value: 'progress',  label: 'Progress Payment' },
                { value: 'variation', label: 'Variation (extra work beyond contract)' },
                { value: 'final',     label: 'Final Invoice' },
              ]}
              disabled={!!editingInvoice}
              data-testid="invoice-type-select"
            />
            <Input
              label="Invoice Number"
              value={invoiceForm.invoice_number}
              onChange={(e) => setInvoiceForm({...invoiceForm, invoice_number: e.target.value})}
              placeholder="INV-001 / VAR-001"
              data-testid="invoice-number-input"
            />
            <Input
              label="Description"
              value={invoiceForm.description}
              onChange={(e) => setInvoiceForm({...invoiceForm, description: e.target.value})}
              placeholder={
                invoiceForm.invoice_type === 'progress' ? 'e.g. Progress #1 — 30% deposit'
                : invoiceForm.invoice_type === 'variation' ? 'e.g. Extra concrete pour — Variation #1'
                : 'e.g. Final invoice on completion'
              }
              data-testid="invoice-description-input"
            />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Invoice Date" type="date" value={invoiceForm.invoice_date} onChange={(e) => setInvoiceForm({...invoiceForm, invoice_date: e.target.value})} data-testid="invoice-date-input" />
              <Input label="Due Date" type="date" value={invoiceForm.due_date} onChange={(e) => setInvoiceForm({...invoiceForm, due_date: e.target.value})} data-testid="invoice-due-date-input" />
            </div>
            <Input
              label="Amount ($) *"
              type="number" step="0.01" min="0.01"
              value={invoiceForm.invoice_amount}
              onChange={(e) => setInvoiceForm({...invoiceForm, invoice_amount: e.target.value})}
              placeholder="0.00"
              data-testid="invoice-amount-input"
            />
            <Textarea label="Notes" value={invoiceForm.notes} onChange={(e) => setInvoiceForm({...invoiceForm, notes: e.target.value})} placeholder="Additional notes..." data-testid="invoice-notes-input" />
            <div className="flex gap-2">
              <Button onClick={handleSaveInvoice} data-testid="save-invoice-btn">
                {editingInvoice ? 'Save Changes' : 'Create Invoice'}
              </Button>
              <Button variant="ghost" onClick={() => { setShowInvoiceModal(false); setEditingInvoice(null); }} data-testid="cancel-invoice-btn">Cancel</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Record payment modal */}
      {showPaymentModal && paymentTargetInvoice && (
        <Modal
          isOpen={true}
          onClose={() => setShowPaymentModal(false)}
          title={`Record Payment — ${paymentTargetInvoice.invoice_number || paymentTargetInvoice.id}`}
        >
          <div className="space-y-4" data-testid="payment-modal">
            <div className="rounded-lg bg-slate-50 p-3 text-sm">
              <div>Invoice amount: <strong>{money(paymentTargetInvoice.invoice_amount)}</strong></div>
              <div>Already paid: <strong className="text-green-600">{money(paymentTargetInvoice.amount_paid)}</strong></div>
              <div>Outstanding: <strong className="text-orange-600">{money((paymentTargetInvoice.invoice_amount || 0) - (paymentTargetInvoice.amount_paid || 0))}</strong></div>
            </div>
            <Input
              label="Payment Amount ($) *"
              type="number" step="0.01" min="0.01"
              value={paymentForm.amount}
              onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
              placeholder="0.00"
              data-testid="payment-amount-input"
            />
            <Input label="Payment Date" type="date" value={paymentForm.payment_date} onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})} data-testid="payment-date-input" />
            <Select
              label="Payment Method"
              value={paymentForm.payment_method}
              onChange={(e) => setPaymentForm({...paymentForm, payment_method: e.target.value})}
              options={[
                { value: '', label: '-- Select --' },
                { value: 'bank_transfer', label: 'Bank Transfer' },
                { value: 'cheque', label: 'Cheque' },
                { value: 'cash', label: 'Cash' },
                { value: 'credit_card', label: 'Credit Card' },
                { value: 'other', label: 'Other' },
              ]}
              data-testid="payment-method-select"
            />
            <Input label="Reference" value={paymentForm.reference} onChange={(e) => setPaymentForm({...paymentForm, reference: e.target.value})} placeholder="BSB/ref number" data-testid="payment-reference-input" />
            <Textarea label="Notes" value={paymentForm.notes} onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})} data-testid="payment-notes-input" />
            <div className="flex gap-2">
              <Button onClick={handleSavePayment} data-testid="save-payment-btn">Record Payment</Button>
              <Button variant="ghost" onClick={() => setShowPaymentModal(false)} data-testid="cancel-payment-btn">Cancel</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default InvoicingPage;
